/**
 * harness/useProgressiveFeed.js
 * T-Cron-2026-10-28 20:00 晚间 — 改进清单 #13「性能:虚拟滚动 / 触底加载更多」
 *
 * 为什么需要(线上实测,不是理论推测):
 *   线上 canonical 的真实数字(浏览器实测):
 *     .post-card = 473 张   DOM 节点 = 22,229 个   outerHTML = 1.28 MB
 *     FCP = 1704ms   DCL = 1592ms   .app-main scrollHeight = 94,694px
 *   也就是说:**首屏一次性挂载了全部 473 张卡 + 22k 个 DOM 节点**。
 *   batch.css 里的 `content-visibility: auto` 只省了「视口外的绘制」,
 *   但 React 仍然要为 473 张卡创建组件、创建 DOM、绑事件 —— 这部分是
 *   **React 渲染 + DOM 构建成本**,content-visibility 管不到。
 *
 * 本 hook 的做法 = 「分组级虚拟滚动 + 触底加载更多」:
 *   ① 初始只挂载前 N 个日期分组(默认 8 组 ≈ 85 张卡),其余按需挂载;
 *   ② 滚到底部哨兵 → 自动追加下一批(触底加载更多);
 *   ③ 窗口超过上限(默认 16 组)→ 把**最上面**的分组卸载,
 *      并**补偿滚动位置**(卸载多少像素就往下滚多少像素)⇒ 视觉零跳动;
 *   ④ 用户想看更早的内容 → 点「加载更早的内容」→ 对称地恢复并补偿。
 *
 * 🔴 为什么用「补偿滚动」而不是「占位 spacer」:
 *   传统虚拟列表用一堆 <div style="height:XXXpx"> 占位。本项目的分组高度
 *   **极不均匀**(6~29 张卡 ⇒ 2~10 行 grid ⇒ 高度差 3~5 倍),估算占位高度
 *   必然失准 ⇒ 滚动条会「呼吸」抖动。改成「卸载即补偿」:
 *     卸载前量出真实布局高度 H,卸载后 scroller.scrollTop += H
 *   ⇒ 视口里的像素完全不动,且不需要任何高度估算。
 *
 * ⚠️ 保持既有行为(逐条):
 *   · 日期分组顺序 / data-screen-label / h2.date-bookmark 全部不变;
 *   · 筛选变化 → 窗口重置到顶部并把滚动位置拉回顶部(渐进渲染下
 *     不重置会停在空白区,这是**必须**的,不是可选优化);
 *   · 收藏/切换版本不会重置窗口(靠「内容签名」而不是数组引用判断);
 *   · Header 的「导出当前可见 473 篇」口径不变(导出的是 store.filtered,
 *     不是已挂载的窗口)⇒ 导出内容不会因为虚拟滚动而少一半。
 *
 * 注意:本文件是 .js(不是 .jsx)⇒ **必须显式 import React**
 * (vite.config.js 的 esbuild.jsxInject 只对 .jsx 生效)。
 */
import React from 'react';

// 首屏挂载的分组数(8 组 ≈ 85 张卡 ≈ 3.8k DOM 节点,而不是 22k)
const DEFAULT_INITIAL = 8;
// 每次「触底 / 点按钮」追加的分组数
const DEFAULT_STEP = 8;
// 同时挂载的分组上限,超出就把最上面的卸载掉
const DEFAULT_MAX_WINDOW = 16;
// 恢复历史分组时的兜底高度(正常情况下都有实测值)
const FALLBACK_GROUP_H = 520;
// 单次级联加载的次数上限(防止极端视口下一口气把全部内容挂上)
const MAX_CASCADE = 24;

function useProgressiveFeed(groups, opts = {}) {
  const {
    initial = DEFAULT_INITIAL,
    step = DEFAULT_STEP,
    maxWindow = DEFAULT_MAX_WINDOW,
    scrollRootRef = null,
  } = opts;

  const totalGroups = groups.length;

  // 窗口 = [from, to) 的分组下标区间
  const [win, setWin] = React.useState(() => ({
    from: 0,
    to: Math.min(initial, totalGroups),
  }));
  // 镜像到 ref:回调里要读「最新」的窗口,但 setState updater 必须保持纯函数
  const winRef = React.useRef(win);
  winRef.current = win;

  // 已卸载分组的实测高度(卸载时量,恢复时用来补偿滚动)
  const heightsRef = React.useRef(new Map());
  // 待补偿的滚动位移(px)——**加到 scrollTop 上的值**:
  //   卸载上方内容 ⇒ scrollTop -= A(视口内容随之下移 A,scrollTop 同步下移)
  //   恢复上方内容 ⇒ scrollTop += A(视口内容随之上移 A,scrollTop 同步上移)
  // 符号搞反会让滚动位置「跳」一下 —— 这是虚拟滚动最典型的翻车点。
  const pendingAdjustRef = React.useRef(0);
  const containerRef = React.useRef(null);
  const bottomSentinelRef = React.useRef(null);
  const cascadeRef = React.useRef(0);
  const pumpRef = React.useRef(null);

  // ── 内容签名:只有「可见集合真的变了」才重置窗口 ────────────────────────
  // 用数组引用判断会被收藏/版本切换误伤(那些会重建 filtered ⇒ 重建 groups),
  // 用「日期:篇数」拼接的签名则只在筛选真的变了时才变。
  const signature = React.useMemo(
    () => groups.map((g) => g.date + ':' + g.posts.length).join('|'),
    [groups]
  );
  const sigRef = React.useRef(signature);
  React.useEffect(() => {
    if (sigRef.current === signature) return;
    sigRef.current = signature;
    pendingAdjustRef.current = 0;
    heightsRef.current.clear();
    const next = { from: 0, to: Math.min(initial, totalGroups) };
    winRef.current = next;
    setWin(next);
    // 渐进渲染下,换筛选后必须回到顶部 —— 否则视口停在「还没挂载的空白区」。
    const scroller = scrollRootRef && scrollRootRef.current;
    if (scroller) scroller.scrollTop = 0;
  }, [signature, initial, totalGroups, scrollRootRef]);

  // ── 卸载前的真实高度测量 ──────────────────────────────────────────────
  const measureTop = React.useCallback((n) => {
    const root = containerRef.current;
    if (!root) return 0;
    const nodes = root.querySelectorAll('[data-feed-group]');
    let freed = 0;
    for (let i = 0; i < n && i < nodes.length; i++) {
      const el = nodes[i];
      const date = el.getAttribute('data-feed-group');
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) heightsRef.current.set(date, h);
      freed += h;
    }
    return freed;
  }, []);

  // ── 触底加载更多(向下追加) ────────────────────────────────────────────
  const loadMore = React.useCallback((count) => {
    const w = winRef.current;
    if (w.to >= totalGroups) return false;
    const n = Math.min(count, totalGroups - w.to);
    let nextFrom = w.from;
    const nextTo = w.to + n;
    // 窗口上限:超出就把最上面的分组卸载(随后补偿滚动,视觉不动)
    const excess = (nextTo - nextFrom) - maxWindow;
    if (excess > 0) {
      nextFrom += excess;
      // 卸载了上方 A 像素的内容 ⇒ scrollTop 要**减去** A,否则视口会往前跳。
      pendingAdjustRef.current -= measureTop(excess);
    }
    const next = { from: nextFrom, to: nextTo };
    winRef.current = next;
    setWin(next);
    return true;
  }, [totalGroups, maxWindow, measureTop]);

  // ── 加载更早的内容(向上恢复) ──────────────────────────────────────────
  const loadEarlier = React.useCallback((count) => {
    const w = winRef.current;
    if (w.from <= 0) return false;
    const n = Math.min(count, w.from);
    let restored = 0;
    for (let i = w.from - n; i < w.from; i++) {
      const g = groups[i];
      if (!g) continue;
      restored += heightsRef.current.get(g.date) || FALLBACK_GROUP_H;
    }
    pendingAdjustRef.current += restored;
    const next = { from: w.from - n, to: w.to };
    winRef.current = next;
    setWin(next);
    return true;
  }, [groups]);

  // ── 滚动补偿:卸载多少像素就往下滚多少像素,恢复则相反 ──────────────────
  React.useLayoutEffect(() => {
    const adj = pendingAdjustRef.current;
    if (!adj) return;
    pendingAdjustRef.current = 0;
    const scroller = scrollRootRef && scrollRootRef.current;
    if (scroller) scroller.scrollTop += adj;
  });

  // ── 哨兵判定:接近底部就追加 ──────────────────────────────────────────
  // ⚠️ checkBottom 与 schedulePump 互相调用 ⇒ 用 ref 打断循环依赖,
  //    否则 useCallback 的依赖数组会形成「谁也更新不了谁」的死结,
  //    并且拿到的是过期的 totalGroups / loadMore。
  const checkBottomRef = React.useRef(() => {});
  const pumpRefFn = React.useRef(() => {});

  const checkBottom = React.useCallback(() => {
    const el = bottomSentinelRef.current;
    const scroller = scrollRootRef && scrollRootRef.current;
    if (!el || !scroller) return;
    if (winRef.current.to >= totalGroups) return;
    const r = el.getBoundingClientRect();
    const sr = scroller.getBoundingClientRect();
    if (r.top <= sr.bottom + 240) {
      if (loadMore(step)) pumpRefFn.current();
    }
  }, [loadMore, step, totalGroups, scrollRootRef]);
  checkBottomRef.current = checkBottom;

  // 级联:追加后哨兵可能仍在视野内 → 下一帧再判定一次(有次数上限)
  const schedulePump = React.useCallback(() => {
    if (pumpRef.current) return;
    pumpRef.current = requestAnimationFrame(() => {
      pumpRef.current = null;
      if (cascadeRef.current > MAX_CASCADE) return;
      cascadeRef.current += 1;
      checkBottomRef.current();
    });
  }, []);
  pumpRefFn.current = schedulePump;

  // 触底自动加载
  React.useEffect(() => {
    const el = bottomSentinelRef.current;
    const scroller = scrollRootRef && scrollRootRef.current;
    if (!el || !scroller) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      const onScroll = () => checkBottomRef.current();
      scroller.addEventListener('scroll', onScroll, { passive: true });
      return () => scroller.removeEventListener('scroll', onScroll);
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            checkBottomRef.current();
          } else {
            // 用户滚离底部 ⇒ 级联计数归零(下次到底可以重新级联)
            cascadeRef.current = 0;
          }
        }
      },
      { root: scroller, rootMargin: '400px 0px 0px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [scrollRootRef, totalGroups]);

  React.useEffect(() => () => {
    if (pumpRef.current) cancelAnimationFrame(pumpRef.current);
  }, []);

  const windowGroups = React.useMemo(
    () => groups.slice(win.from, win.to),
    [groups, win.from, win.to]
  );

  const mountedPosts = React.useMemo(
    () => windowGroups.reduce((acc, g) => acc + g.posts.length, 0),
    [windowGroups]
  );
  const totalPosts = React.useMemo(
    () => groups.reduce((acc, g) => acc + g.posts.length, 0),
    [groups]
  );

  return {
    containerRef,
    bottomSentinelRef,
    windowGroups,
    hasMoreBelow: win.to < totalGroups,
    hasMoreAbove: win.from > 0,
    mountedGroups: win.to - win.from,
    totalGroups,
    mountedPosts,
    totalPosts,
    loadMore: () => loadMore(step),
    loadEarlier: () => loadEarlier(step),
    // 读屏播报:告诉 AT 用户「列表是虚拟滚动的,当前挂载了多少」
    statusText:
      '虚拟滚动已启用。已加载 ' + mountedPosts + ' / ' + totalPosts + ' 篇,'
      + '共 ' + win.to + ' / ' + totalGroups + ' 个日期分组。'
      + (win.to < totalGroups ? '继续向下滚动会自动加载更多。' : ''),
  };
}

window.useProgressiveFeed = useProgressiveFeed;

export default useProgressiveFeed;
